import { Flex, VStack } from "@chakra-ui/react";
import React, { forwardRef } from "react";

import {
  SegmentAllRestrictionsWarning,
  SegmentStatelessRestrictionsWarning,
} from "@/common/components/Rule/RuleRestrictions";
import { SegmentPicker } from "@/common/components/SegmentPicker";
import { SegmentDisplay } from "@/company/components/SegmentDisplay";
import { useSegments } from "@/company/data/useSegments";

import { RuleSimpleSelection } from "./RuleSimpleSelectionProps";
import { SelectorProps, sharedProps } from "./RuleSimpleSelectorTypes";

export const RuleSimpleSegmentSelector = forwardRef<
  HTMLButtonElement,
  SelectorProps
>(({ name, value, isReadOnly, onChange, onBlur }, ref) => {
  const { data: segments, isLoading } = useSegments();
  const selectedSegments = value?.map(
    (id) => segments?.find((s) => s.id === id) ?? { id, name: id },
  );

  return (
    <Flex align="center" gap={2} wrap="wrap">
      {selectedSegments?.map((segment) => (
        <RuleSimpleSelection
          key={segment.id}
          isLoading={isLoading}
          readOnly={isReadOnly}
          onRemove={() =>
            value && onChange
              ? onChange(value.filter((id) => id !== segment.id))
              : undefined
          }
        >
          <SegmentDisplay segment={segment} />
        </RuleSimpleSelection>
      ))}
      {!isReadOnly && (
        <SegmentPicker
          {...sharedProps}
          ref={ref}
          filterFn={(s) => {
            if (value?.includes(s.id)) return "hidden";
            if (s.isAllSegment || !s.stateless) return "unsupported";
            return "supported";
          }}
          name={name}
          unsupportedReason={
            <VStack>
              <SegmentAllRestrictionsWarning />
              <SegmentStatelessRestrictionsWarning />
            </VStack>
          }
          showArrow
          showPieChart
          onBlur={onBlur}
          onChange={(newSelection) => {
            if (!newSelection) return;
            onChange?.(value?.concat(newSelection) ?? [newSelection]);
          }}
        />
      )}
    </Flex>
  );
});
RuleSimpleSegmentSelector.displayName = "RuleSimpleSegmentSelector";
